import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Single-button welcome-page music player.
 *
 * Behaviour:
 *  - Fetches the currently active site track from `/api/music/current`.
 *  - When a track exists, mounts a hidden <audio loop muted autoplay> element
 *    (browsers permit muted autoplay), and renders ONE speaker icon button
 *    next to the brand logo.
 *  - Clicking the icon toggles mute/unmute (no separate play button).
 *  - Renders nothing if no track is active or backend reports disabled.
 */
export const WelcomeMusicPlayer = () => {
  const [track, setTrack] = useState(null);
  // Optimistically show as unmuted; the boot effect corrects this if the
  // browser blocks unmuted autoplay (then it falls back to muted+listen).
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/music/current`)
      .then((r) => {
        if (!cancelled && r.data?.enabled && r.data?.url) {
          const url = r.data.url.startsWith('http') ? r.data.url : `${BACKEND_URL}${r.data.url}`;
          setTrack({ ...r.data, url });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !track) return;
    a.loop = true;
    a.volume = 0.55;

    // Try unmuted autoplay first. Browsers will reject if the user hasn't
    // interacted yet — in that case fall back to muted autoplay and wait
    // for the first interaction anywhere on the page to flip mute off.
    const tryUnmutedPlay = async () => {
      try {
        a.muted = false;
        await a.play();
        setMuted(false);
        return true;
      } catch {
        return false;
      }
    };

    const startMutedFallback = async () => {
      try {
        a.muted = true;
        await a.play();
        setMuted(true);
      } catch {
        // Could not even play muted — give up silently
      }
    };

    const onFirstInteraction = async () => {
      const ok = await tryUnmutedPlay();
      if (ok) {
        window.removeEventListener('pointerdown', onFirstInteraction);
        window.removeEventListener('keydown', onFirstInteraction);
      }
    };

    const boot = async () => {
      const ok = await tryUnmutedPlay();
      if (!ok) {
        await startMutedFallback();
        // Unmute on the very first user gesture
        window.addEventListener('pointerdown', onFirstInteraction, { once: false });
        window.addEventListener('keydown', onFirstInteraction, { once: false });
      }
    };

    if (a.readyState >= 2) boot();
    else a.addEventListener('loadeddata', boot, { once: true });

    return () => {
      window.removeEventListener('pointerdown', onFirstInteraction);
      window.removeEventListener('keydown', onFirstInteraction);
    };
  }, [track]);

  const toggleMute = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.muted) {
      a.muted = false;
      try {
        await a.play();
      } catch {
        // some browsers still block; user gesture should be enough
      }
      setMuted(false);
    } else {
      a.muted = true;
      setMuted(true);
    }
  };

  if (!track) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src={track.url}
        preload="auto"
        // eslint-disable-next-line react/no-unknown-property
        playsInline
        data-testid="welcome-music-audio"
      />
      <button
        type="button"
        onClick={toggleMute}
        aria-pressed={!muted}
        aria-label={muted ? `Play music: ${track.title}` : `Mute music: ${track.title}`}
        title={muted ? `Play music · ${track.title}` : `Mute · ${track.title}`}
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300
          ${muted
            ? 'border-border bg-card text-ash hover:text-gold hover:border-gold'
            : 'border-gold/60 bg-gold/10 text-gold hover:bg-gold/15'}
        `}
        data-testid="welcome-music-toggle"
      >
        {muted ? (
          <SpeakerSlash size={18} weight="duotone" />
        ) : (
          <SpeakerHigh size={18} weight="duotone" />
        )}
        {!muted && (
          <span
            aria-hidden="true"
            className="absolute -inset-px rounded-full border border-gold/40 animate-ping pointer-events-none"
          />
        )}
      </button>
    </>
  );
};
